// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import bemify, { toModifiers } from 'bemify-js';

import { createKeyHandler } from '@zapier/common-utils';

import Icon from 'app/common/components/Icon';
import Link from 'app/common/components/Link';
import MegaMenu from 'app/viewport/components/Header/MegaMenu';
import { normalizePathname } from 'app/common/utils/UrlUtils';
import 'app/viewport/components/Header/HeaderLinks.scss';

import type { KindType } from 'app/common/types';
import type { MegaMenuLinkCollection } from 'app/viewport/components/Header/MegaMenu';

const MEGA_MENU_CLOSE_TIME = 10; // in ms
const MEGA_MENU_OPEN_TIME = 300; // in ms

// Using `v2` here to avoid conflicts with sassy :|
const bem = bemify('navigation-links-v2');

type MegaMenuPageCollection = {
  key: string,
  collections: MegaMenuLinkCollection[],
};

type MegaMenuVisibleState = string | null;

export type HeaderLinkItemProps = {
  title: string,
  href: string,
  isActive: boolean,
  onToggleMegaMenu: (key: MegaMenuVisibleState) => void,
  onToggleMegaMenuWithDelay: (key: MegaMenuVisibleState, delay: number) => void,
  // Used to set anchor tag `rel` to `external`, this forces `redux-router-kit`
  // to refresh the browser when navigating to this link, e.g. avoid client-side
  // routing.
  forceRefreshOnExit: boolean,
  kind?: KindType,
  relatedMatches: string[],
  onClick: (e: SyntheticEvent<>) => void,
  childLinks?: MegaMenuPageCollection,
  visibleMegaMenu: MegaMenuVisibleState,
  isMobileMenu?: boolean,
};

const HeaderLinkItem = (props: HeaderLinkItemProps) => {
  const {
    title,
    href,
    kind,
    isActive,
    forceRefreshOnExit,
    childLinks = {},
    onClick,
    onToggleMegaMenu,
    onToggleMegaMenuWithDelay,
    visibleMegaMenu,
    isMobileMenu,
  } = props;

  // The `href` acts as an ID here, we use it to both identify the mega menu
  // link container and as the URL that the header text _may_ navigate to ( on
  // mobile the link opens the mega menu, on desktop it is a link).
  const isLinkActive = href === visibleMegaMenu;
  const hasChildLinks = !_.isEmpty(childLinks.collections);

  const headerNode = (() => {
    // If this is the mobile menu, we need to return a different DOM structure
    // to support the accordion menu.
    if (isMobileMenu && hasChildLinks) {
      return (
        <div
          className={bem('__link', kind, isActive ? '--active' : '')}
          onClick={() => onToggleMegaMenu(isLinkActive ? null : href)}
          onKeyDown={createKeyHandler(['enter'], () =>
            onToggleMegaMenu(isLinkActive ? null : href)
          )}
          role="button"
          tabIndex="0"
        >
          <span className={bem('__inner-link', kind)}>{title}</span>
          <Icon
            className={bem('__arrow-down')}
            type={isLinkActive ? 'arrow-up' : 'arrow-down'}
          />
        </div>
      );
    }

    return (
      <Link
        className={bem(
          '__link',
          kind,
          isActive ? '--active' : '',
          isLinkActive ? '--visible-mega' : ''
        )}
        data-content={title /* CSS hack used by --selectable */}
        href={href}
        onClick={onClick}
        onMouseEnter={
          !isMobileMenu
            ? () => {
                onToggleMegaMenuWithDelay(href, MEGA_MENU_OPEN_TIME);
              }
            : _.noop
        }
        shouldRefreshPage={forceRefreshOnExit}
      >
        <span className={bem('__inner-link', kind)}>{title}</span>
      </Link>
    );
  })();

  return hasChildLinks ? (
    <div className={bem('__item', kind)}>
      {headerNode}
      <MegaMenu
        kind={isLinkActive ? 'visible' : 'hidden'}
        links={childLinks.collections}
        renderHeaderLink={
          !isMobileMenu
            ? null
            : () => <a href={href}>Browse all {title.toLowerCase()}</a>
        }
      />
    </div>
  ) : (
    <div className={bem('__item', kind)}>{headerNode}</div>
  );
};

type HeaderLinksProps = {
  currentUrl: string,
  forceRefreshOnExit: boolean,
  links: HeaderLinkItemProps[],
  kind?: KindType,
  isMobileMenu?: boolean,
  onClickLink: () => void,
};

type NavLinksState = {
  visibleMegaMenu: MegaMenuVisibleState,
};

class HeaderLinks extends Component<HeaderLinksProps, NavLinksState> {
  state = {
    visibleMegaMenu: null,
  };

  megaMenuTrigger: TimeoutID | null;

  onSetMegaMenuVisbility = (key: MegaMenuVisibleState) => {
    this.setState({
      visibleMegaMenu: key,
    });
  };

  onSetMegaMenuVisbilityWithDelay = (
    key: MegaMenuVisibleState,
    delay: number
  ) => {
    if (this.megaMenuTrigger) {
      clearTimeout(this.megaMenuTrigger);
    }

    if (!this.props.isMobileMenu) {
      this.megaMenuTrigger = setTimeout(() => {
        this.setState({
          visibleMegaMenu: key,
        });

        this.megaMenuTrigger = null;
      }, delay);
    }
  };

  render() {
    const {
      currentUrl,
      links,
      forceRefreshOnExit,
      kind,
      onClickLink,
      isMobileMenu,
    } = this.props;

    // Passing `true` to `normalizePathname` to ensure that any query / hash
    // string is retained
    const currentUrlNormalized = normalizePathname(currentUrl, true);

    const linkElements = links.map((linkProps, idx) => {
      const urlInRelatedMatches = linkProps.relatedMatches.find(relatedMatch =>
        currentUrlNormalized.startsWith(relatedMatch)
      );
      const isActive: boolean = Boolean(
        currentUrlNormalized === linkProps.href || urlInRelatedMatches
      );

      return (
        <HeaderLinkItem
          isMobileMenu={isMobileMenu}
          key={idx}
          {...linkProps}
          forceRefreshOnExit={forceRefreshOnExit}
          isActive={isActive}
          onClick={e => {
            if (linkProps.onClick) {
              linkProps.onClick(e);
            }

            onClickLink();
          }}
          onToggleMegaMenu={this.onSetMegaMenuVisbility}
          onToggleMegaMenuWithDelay={this.onSetMegaMenuVisbilityWithDelay}
          visibleMegaMenu={this.state.visibleMegaMenu}
        />
      );
    });

    return (
      <nav
        className={bem(toModifiers(kind))}
        onMouseLeave={() =>
          this.onSetMegaMenuVisbilityWithDelay(null, MEGA_MENU_CLOSE_TIME)
        }
      >
        {linkElements}
      </nav>
    );
  }
}

export default HeaderLinks;
